import React from "react";
import { createRoot } from "react-dom/client";
import RentCurvesSpinner, { EmbedConfig, CropData } from "./RentCurvesSpinner";

// Type declarations for the global namespace
declare global {
  interface Window {
    RentCurvesSpinner: {
      init: (elementId: string, config?: EmbedConfig) => void;
      initFromDataAttributes: (elementId: string) => void;
      unmount: (elementId: string) => void;
    };
  }
}

// Track mounted instances to prevent double-mounting
const mountedInstances = new Map<string, ReturnType<typeof createRoot>>();

// Parse data attributes from container element
function parseDataAttributes(element: HTMLElement): EmbedConfig {
  const crops: CropData[] = [];

  // Check for single crop data attributes
  const cropType = element.dataset.cropType;
  if (cropType) {
    crops.push({
      name: cropType,
      state: element.dataset.location || "",
      price: parseFloat(element.dataset.price || "0"),
      harvestYield: parseFloat(element.dataset.harvestYield || "0"),
      highYield: parseFloat(element.dataset.highYield || "0"),
      cost: parseFloat(element.dataset.cost || "0"),
      avgYield: parseFloat(element.dataset.avgYield || "0"),
    });
  }

  // Check for JSON crops data
  const cropsJson = element.dataset.crops;
  if (cropsJson) {
    try {
      const parsedCrops = JSON.parse(cropsJson);
      if (Array.isArray(parsedCrops)) {
        crops.push(...parsedCrops);
      }
    } catch (e) {
      console.error("RentCurvesSpinner: Failed to parse crops JSON", e);
    }
  }

  return {
    crops: crops.length > 0 ? crops : undefined,
    primaryColor: element.dataset.primaryColor,
    showInsurance: element.dataset.showInsurance !== "false",
  };
}

// Initialize spinner with config object
function init(elementId: string, config?: EmbedConfig) {
  const container = document.getElementById(elementId);
  if (!container) {
    console.error(`RentCurvesSpinner: Element with id "${elementId}" not found`);
    return;
  }

  // Prevent double-mounting
  if (mountedInstances.has(elementId)) {
    console.warn(`RentCurvesSpinner: "${elementId}" already mounted. Use unmount() first to re-initialize.`);
    return;
  }

  const root = createRoot(container);
  mountedInstances.set(elementId, root);
  root.render(
    React.createElement(RentCurvesSpinner, { config })
  );
}

// Initialize spinner from data attributes on container element
function initFromDataAttributes(elementId: string) {
  const container = document.getElementById(elementId);
  if (!container) {
    console.error(`RentCurvesSpinner: Element with id "${elementId}" not found`);
    return;
  }

  const config = parseDataAttributes(container);
  init(elementId, config);
}

// Unmount a spinner instance for cleanup
function unmount(elementId: string) {
  const root = mountedInstances.get(elementId);
  if (root) {
    root.unmount();
    mountedInstances.delete(elementId);
  }
}

// Expose global API
window.RentCurvesSpinner = {
  init,
  initFromDataAttributes,
  unmount,
};

// Auto-initialize any elements with data-rentcurves-spinner attribute
document.addEventListener("DOMContentLoaded", () => {
  const autoInitElements = document.querySelectorAll("[data-rentcurves-spinner]");
  autoInitElements.forEach((element) => {
    if (element.id) {
      initFromDataAttributes(element.id);
    } else {
      // Generate an ID if none exists
      const id = `rentcurves-spinner-${Math.random().toString(36).substr(2, 9)}`;
      element.id = id;
      initFromDataAttributes(id);
    }
  });
});

export { init, initFromDataAttributes, unmount };